import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import { signIn } from "../../lib/auth-client";

interface LoginPageProps {
  onNavigate: (page: "signup" | "forgot-password" | "dashboard") => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onNavigate }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [_showCursor, setShowCursor] = useState(true);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("ERROR: ALL FIELDS REQUIRED");
      return;
    }

    setLoading(true);

    try {
      const result = await signIn.email(
        {
          email,
          password,
          rememberMe,
        },
        {
          onError: (ctx) => {
            setError(`ERROR: ${ctx.error.message?.toUpperCase() || "AUTHENTICATION FAILED"}`);
            setLoading(false);
          },
        }
      );

      // On success, keep loading state - App.tsx will redirect once useSession() updates
      if (result.error) {
        setError(`ERROR: ${result.error.message?.toUpperCase() || "AUTHENTICATION FAILED"}`);
        setLoading(false);
      }
    } catch (err) {
      setError("ERROR: CONNECTION FAILED. TRY AGAIN.");
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement 2FA verification
    setError("2FA NOT YET IMPLEMENTED");
  };

  if (show2FA) {
    return (
      <AuthLayout title="*** TWO-FACTOR AUTHENTICATION ***">
        <form onSubmit={handle2FASubmit} className="space-y-4">
          <div className="text-green-500 text-sm mb-4">
            ENTER THE 6-DIGIT CODE SENT TO YOUR EMAIL:
          </div>

          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="text"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-[0.5em] text-center text-xl"
              placeholder="000000"
              autoFocus
              maxLength={6}
            />
          </div>

          {error && (
            <div className="border-2 border-red-500 bg-black p-3 text-center">
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || twoFactorCode.length !== 6}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "[VERIFYING...]" : "[VERIFY CODE]"}
          </button>

          <button
            type="button"
            onClick={() => setShow2FA(false)}
            className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
          >
            [BACK TO LOGIN]
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="*** USER AUTHENTICATION ***" showBootSequence>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email field */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            EMAIL:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="user@domain.com"
              autoFocus
            />
          </div>
        </div>

        {/* Password field */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            PASSWORD:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="********"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="px-3 text-green-600 hover:text-green-400"
            >
              {showPassword ? "[HIDE]" : "[SHOW]"}
            </button>
          </div>
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="accent-green-500"
          />
          <label htmlFor="rememberMe" className="text-green-600 text-sm cursor-pointer">
            REMEMBER THIS TERMINAL
          </label>
        </div>

        {/* Error message */}
        {error && (
          <div
            className="border-2 border-red-500 bg-black p-3 text-center"
            style={{ animation: "shake 0.5s ease-in-out" }}
          >
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.4)" }}
        >
          {loading ? "[AUTHENTICATING...]" : "[LOGIN]"}
        </button>

        {/* Links */}
        <div className="flex justify-between text-sm mt-4">
          <button
            type="button"
            onClick={() => onNavigate("signup")}
            className="text-green-600 hover:text-green-400 underline"
          >
            CREATE ACCOUNT
          </button>
          <button
            type="button"
            onClick={() => onNavigate("forgot-password")}
            className="text-green-600 hover:text-green-400 underline"
          >
            FORGOT PASSWORD?
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
